import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, IconButton, Typography } from '@mui/material'
import { VehiclePhoto } from '@otw/models'
import React, { useRef, useState } from 'react'
import { ImageMagnifier } from '@/components/ImageMagnifier'
import { isVideoFileName } from '@/components/inventory/utils/inventoryMedia'

const SWIPE_THRESHOLD_PX = 50

interface CarouselTabProps {
  photos: VehiclePhoto[] | undefined
  isMobile?: boolean
  magnifierEnabled?: boolean
}

const CarouselTab: React.FC<CarouselTabProps> = ({ photos, isMobile = false, magnifierEnabled = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  const list = photos ?? []
  const hasPhotos = list.length > 0
  const current = hasPhotos ? list[currentIndex] : null

  const goPrev = () => {
    setCurrentIndex(prev => (prev <= 0 ? list.length - 1 : prev - 1))
  }

  const goNext = () => {
    setCurrentIndex(prev => (prev >= list.length - 1 ? 0 : prev + 1))
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return
    touchStartX.current = e.targetTouches[0].clientX
    touchEndX.current = null
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return
    touchEndX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!isMobile || touchStartX.current == null || touchEndX.current == null) return
    const diff = touchStartX.current - touchEndX.current
    if (Math.abs(diff) >= SWIPE_THRESHOLD_PX) {
      if (diff > 0) goNext()
      else goPrev()
    }
    touchStartX.current = null
    touchEndX.current = null
  }

  if (!hasPhotos) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 420,
          color: 'text.secondary',
        }}
      >
        <Typography>No photos or videos to display in carousel.</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: 1,
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          width: '100%',
          minHeight: 0,
          flex: 1,
        }}
      >
        {!isMobile && (
          <IconButton
            onClick={goPrev}
            size='large'
            sx={{
              flexShrink: 0,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            aria-label='Previous item'
          >
            <ChevronLeftIcon fontSize='large' />
          </IconButton>
        )}

        <Box
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
            minHeight: isMobile ? 280 : 560,
            touchAction: isMobile ? 'pan-y' : undefined,
          }}
        >
          {current && isVideoFileName(current.name) ? (
            <Box
              component='video'
              src={current.url}
              controls
              playsInline
              sx={{
                maxWidth: '100%',
                maxHeight: isMobile ? 360 : 560,
                borderRadius: 1,
              }}
            />
          ) : isMobile ? (
            <Box
              component='img'
              src={current?.url}
              alt={current?.name ?? `Photo ${currentIndex + 1}`}
              draggable={false}
              sx={{
                maxWidth: '100%',
                maxHeight: 360,
                objectFit: 'contain',
                borderRadius: 1,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          ) : magnifierEnabled ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                maxHeight: 560,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
              }}
            >
              <ImageMagnifier
                key={current?.url}
                src={current?.url ?? ''}
                alt={current?.name ?? `Photo ${currentIndex + 1}`}
                sx={{ width: '100%', height: '100%', maxHeight: 560 }}
                imgSx={{ maxHeight: 560, borderRadius: 1 }}
              />
            </Box>
          ) : (
            <Box
              component='img'
              src={current?.url}
              alt={current?.name ?? `Photo ${currentIndex + 1}`}
              sx={{
                maxWidth: '100%',
                maxHeight: 560,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </Box>

        {!isMobile && (
          <IconButton
            onClick={goNext}
            size='large'
            sx={{
              flexShrink: 0,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            aria-label='Next item'
          >
            <ChevronRightIcon fontSize='large' />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          maxHeight: 100,
          overflow: 'auto',
        }}
      >
        {list.map((photo, index) => (
          <Box
            key={photo.id ?? photo.url}
            component='button'
            type='button'
            onClick={() => setCurrentIndex(index)}
            sx={{
              flexShrink: 0,
              width: 80,
              height: 80,
              p: 0,
              border: 2,
              borderColor: index === currentIndex ? 'primary.main' : 'transparent',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              bgcolor: 'transparent',
              '&:hover': { opacity: 0.9 },
            }}
          >
            {isVideoFileName(photo.name) ? (
              <Box
                component='video'
                src={photo.url}
                muted
                playsInline
                preload='metadata'
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                component='img'
                src={photo.url}
                alt={photo.name ?? `Photo ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default CarouselTab
